/* SIZESOFTX
=========================================================================================
* Descripción: Componente de entrenamiento de IA para Computadora
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Selección visual de objeto y entrenamiento por muestras
*      2        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Encuadre de objeto por arrastre para entrenamiento IA
*      3        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Seguimiento local de objeto sin dependencias externas
*      4        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Envoltura por máscara local de canvas
*      5        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Envoltura asistida completa sin máscara por color
*      6        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Envoltura manual por lazo sin modelos externos
*      7        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Lazo libre sin fallback rectangular
*      8        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Rayos Fibonacci multicolor para diagnóstico visual
*      9        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Vista interna de grises y bordes para diagnóstico IA
*      10       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Huella radial RGB y gris desde el centro del lazo
*      11       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Optimización de fluidez del seguimiento radial
*      12       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Corrección de escala del lazo sobre el área visible del video
*      13       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Monitoreo visual de predicción y conteo de muestras
*      14       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Entrenamiento obligado con recorte activo del lazo de seguimiento
*      15       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se ocultan rayos durante el trazo y se muestran al cerrar el lazo
*      16       | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Entrenamiento usa recorte normalizado para cámaras distintas
* ---------------------------------------------------------------------------------------
*/

import { Fragment, useEffect, useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Grid, Card, CardContent, Typography, LinearProgress, Chip, CircularProgress, Stack } from "@mui/material";
import VideocamIcon from '@mui/icons-material/Videocam';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PsychologyIcon from '@mui/icons-material/Psychology';

import SizeSoftXLayout from "../../leyout/SizeSoftXLayout.jsx";
import { useIAEntrenamiento } from "../../../context/IA/IAEntrenamientoContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";
import ModalBuscarArticulosSimple from "../../herramientas/ModalBuscarArticulosSimple";
import ModalBuscarPorFoto from "./ModalBuscarPorFoto";
import MnuIAEntrenamientoActions from "../MnuIAEntrenamientoActions";
import CameraObjectSelectorOverlay from "../CameraObjectSelectorOverlay";
import {
    captureVideoFrame,
    createObjectSelectionFromLasso,
    createSelectionDebugPreview,
    createTrackingTemplate,
    getCameraPoint,
    trackObjectSelection,
} from "../utils/cameraSelection";

export default function FrmEntrenamientoIAPC() {
    const navigate = useNavigate();
    const { isLocked: isFormLocked } = useFormTabLock('entrenamiento_ia_pc', {
        title: 'Entrenamiento IA PC',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Entrenamiento IA PC'} ya está abierto en otra pestaña`);
        }
    });

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const trackingAnimationRef = useRef(null);
    const trackingTemplateRef = useRef(null);
    const objectSelectionRef = useRef(null);
    const selectionAnchorRef = useRef(null);
    const lassoPointsRef = useRef([]);
    const predictionInFlightRef = useRef(false);

    const {
        articuloSeleccionado,
        setArticuloSeleccionado,
        imagenCapturada,
        setImagenCapturada,
        handleGuardarImagenArticulo,
        handleEnviarVectorEntrenamiento,
        handleObtenerInfoAprendizaje,
        handleEliminarEntrenamiento,
        handlePredecir,
        infoAprendizaje,
        imagenArticulo,
        statusBuscarArticuloSimple,
        handleAbrirBuscarArticulo,
        handleCerrarBuscarArticulo,
        handleArticuloSelect,
        medidaSeleccionada,
        setMedidaSeleccionada,
        getMedidasArticulo,
        metricasIA,
        handleObtenerMetricas,
        entrenamientoRed,
        handleEntrenarModelo,
    } = useIAEntrenamiento();

    const handleAbrirBuscarArticuloRef = useRef(handleAbrirBuscarArticulo);
    useEffect(() => {
        handleAbrirBuscarArticuloRef.current = handleAbrirBuscarArticulo;
    }, [handleAbrirBuscarArticulo]);

    const { handleChangeTitle, handleChangeClavePaginaActual, handleOpenAlert } = useLayout();

    useEffect(() => {
        if (isFormLocked) {
            alert("El formulario de Entrenamiento IA PC ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
            setTimeout(() => {
                navigate("/*");
            }, 900);
        }
    }, [isFormLocked]);

    useEffect(() => {
        window.scrollTo(0, 0);
        handleChangeTitle("Entrenamiento IA - PC");
        handleChangeClavePaginaActual("IAEntrenamientoPC");
        iniciarCamara();
        
        return () => {
            detenerCamara();
            if (trackingAnimationRef.current) {
                cancelAnimationFrame(trackingAnimationRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let lastTracking = 0;

        const ejecutarSeguimiento = (timestamp) => {
            if (timestamp - lastTracking > 80 && trackingTemplateRef.current && objectSelectionRef.current && !selectionAnchorRef.current) {
                const nextSelection = trackObjectSelection({
                    videoElement: videoRef.current,
                    currentSelection: objectSelectionRef.current,
                    template: trackingTemplateRef.current,
                    searchRadiusPct: 0.045,
                    searchSteps: 3,
                });

                objectSelectionRef.current = nextSelection;
                setObjectSelection(nextSelection);
                lastTracking = timestamp;
            }

            trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);
        };

        trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);

        return () => {
            if (trackingAnimationRef.current) {
                cancelAnimationFrame(trackingAnimationRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.altKey && event.key.trim().toUpperCase() === 'F1') {
                event.preventDefault();
                event.stopPropagation();
                handleAbrirBuscarArticuloRef.current();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const [loadingAprendizaje, setLoadingAprendizaje] = useState(false);
    const [openBusquedaFoto, setOpenBusquedaFoto] = useState(false);
    const [objectSelection, setObjectSelection] = useState(null);
    const [selectionAnchor, setSelectionAnchor] = useState(null);
    const [selectionDebugPreview, setSelectionDebugPreview] = useState(null);
    const [livePrediction, setLivePrediction] = useState(null);
    const [sampleProgress, setSampleProgress] = useState({ active: false, current: 0, total: 10 });

    useEffect(() => {
        objectSelectionRef.current = objectSelection;
    }, [objectSelection]);

    useEffect(() => {
        if (articuloSeleccionado?.clavearticulo) {
            handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
            const medidas = getMedidasArticulo(articuloSeleccionado);
            if (medidas.length > 0 && !medidas.includes(medidaSeleccionada)) {
                setMedidaSeleccionada(medidas[0]);
            }
        }
    }, [articuloSeleccionado?.clavearticulo]);

    useEffect(() => {
        handleObtenerMetricas("articulos");
    }, []);

    useEffect(() => {
        if (!objectSelection) {
            setSelectionDebugPreview(null);
            return undefined;
        }

        const interval = setInterval(() => {
            setSelectionDebugPreview(createSelectionDebugPreview({
                videoElement: videoRef.current,
                selection: objectSelectionRef.current,
                size: 96,
            }));
        }, 260);

        return () => clearInterval(interval);
    }, [Boolean(objectSelection)]);

    useEffect(() => {
        if (!objectSelection || !articuloSeleccionado || sampleProgress.active) {
            setLivePrediction(null);
            return undefined;
        }

        const interval = setInterval(async () => {
            if (predictionInFlightRef.current || !objectSelectionRef.current) return;
            predictionInFlightRef.current = true;
            try {
                const imagenBase64 = captureVideoFrame({
                    videoElement: videoRef.current,
                    canvasElement: canvasRef.current,
                    selection: objectSelectionRef.current,
                    quality: 0.68,
                });
                if (imagenBase64) {
                    const prediction = await handlePredecir(imagenBase64, { silent: true, minConfidence: 0.90, medida: medidaSeleccionada });
                    setLivePrediction(prediction);
                }
            } finally {
                predictionInFlightRef.current = false;
            }
        }, 2800);

        return () => clearInterval(interval);
    }, [Boolean(objectSelection), articuloSeleccionado?.clavearticulo, sampleProgress.active]);

    

    const detenerCamara = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    const iniciarCamara = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ 
                video: { width: 640, height: 480 } 
            });
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (err) {
            console.error("Error al iniciar cámara:", err);
        }
    };

    const handleArticuloFromFoto = async (articulo, imagen) => {
        await handleArticuloSelect(articulo);
        if (imagen) {
            setImagenCapturada(imagen);
        }
    };

    const handleSeleccionObjetoStart = (event) => {
        if (!videoRef.current) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const anchor = getCameraPoint(event, videoRef.current);
        lassoPointsRef.current = [anchor];
        selectionAnchorRef.current = anchor;
        trackingTemplateRef.current = null;
        const initialSelection = {
            xPct: anchor.xPct,
            yPct: anchor.yPct,
            widthPct: 0,
            heightPct: 0,
            contourPoints: [anchor],
            isDrawing: true,
        };
        objectSelectionRef.current = initialSelection;
        setSelectionAnchor(anchor);
        setObjectSelection(initialSelection);
    };

    const handleSeleccionObjetoMove = (event) => {
        const anchor = selectionAnchorRef.current || selectionAnchor;
        if (!videoRef.current || !anchor) return;
        event.preventDefault();
        const endPoint = getCameraPoint(event, videoRef.current);
        const lastPoint = lassoPointsRef.current[lassoPointsRef.current.length - 1];
        const deltaX = Math.abs((lastPoint?.xPct ?? 0) - endPoint.xPct);
        const deltaY = Math.abs((lastPoint?.yPct ?? 0) - endPoint.yPct);

        if (deltaX > 0.006 || deltaY > 0.006) {
            lassoPointsRef.current = [...lassoPointsRef.current, endPoint];
        }

        const nextSelection = {
            ...createObjectSelectionFromLasso(lassoPointsRef.current, 0.08, true),
        };
        objectSelectionRef.current = nextSelection;
        setObjectSelection(nextSelection);
    };

    const handleSeleccionObjetoEnd = (event) => {
        const anchor = selectionAnchorRef.current || selectionAnchor;
        if (!anchor || !videoRef.current) return;
        event.preventDefault();
        const endPoint = getCameraPoint(event, videoRef.current);
        lassoPointsRef.current = [...lassoPointsRef.current, endPoint];
        const maskSelection = lassoPointsRef.current.length > 4
            ? createObjectSelectionFromLasso(lassoPointsRef.current, 0.08, false)
            : null;
        const trackingTemplate = maskSelection
            ? createTrackingTemplate({
                videoElement: videoRef.current,
                selection: maskSelection,
            })
            : null;
        const finalSelection = maskSelection && trackingTemplate?.radialDebugRays
            ? { ...maskSelection, debugRays: trackingTemplate.radialDebugRays }
            : maskSelection;
        objectSelectionRef.current = finalSelection;
        setObjectSelection(finalSelection);
        trackingTemplateRef.current = trackingTemplate;
        selectionAnchorRef.current = null;
        setSelectionAnchor(null);
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    };

    const getSelectionForTraining = () => {
        const selection = objectSelectionRef.current;
        if (!selection || selection.isDrawing || selection.widthPct <= 0 || selection.heightPct <= 0) {
            return null;
        }

        return selection;
    };

    const capturarImagenBase64 = (quality = 0.82) => {
        const selection = getSelectionForTraining();
        if (!selection) {
            handleOpenAlert("Encierre el artículo con el lazo antes de capturar o entrenar");
            return null;
        }

        return captureVideoFrame({
            videoElement: videoRef.current,
            canvasElement: canvasRef.current,
            selection,
            quality,
        });
    };

    const capturarImagenActual = ({ guardar = true } = {}) => {
        const imagenBase64 = capturarImagenBase64(0.82);

        if (!imagenBase64) return null;

        setImagenCapturada(imagenBase64);

        if (guardar && articuloSeleccionado) {
            handleGuardarImagenArticulo(imagenBase64);
        }

        return imagenBase64;
    };

    const tomarFoto = () => capturarImagenActual({ guardar: true });

    const handleAsignarFoto = async () => {
        if (!articuloSeleccionado?.clavearticulo || loadingAprendizaje) return;
        const selection = getSelectionForTraining();
        if (selection) {
            tomarFoto();
        } else if (imagenCapturada) {
            const img = imagenCapturada;
            setImagenCapturada(null);
            await handleGuardarImagenArticulo(img);
        } else {
            handleOpenAlert("Seleccione el artículo en la cámara o busque por foto");
        }
    };

    const handleRefreshInfo = async () => {
        if (articuloSeleccionado?.clavearticulo) {
            await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        }
    };

    const handleEnviarEntrenamiento = async () => {
        if (!articuloSeleccionado) return;

        const imagenBase64 = capturarImagenBase64(0.82);
        if (!imagenBase64) return;

        setLoadingAprendizaje(true);
        await handleEnviarVectorEntrenamiento(imagenBase64, null, { medida: medidaSeleccionada });
        await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        setLoadingAprendizaje(false);
    };

    const handleEntrenarRed = async () => {
        await handleEntrenarModelo("articulos", 8);
    };

    const esperar = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const getPredictionClave = (prediction) => (
        prediction?.clavearticulo
        || prediction?.claveArticulo
        || prediction?.sClaveArticulo
        || prediction?.SClaveArticulo
        || ""
    ).trim();

    const getPredictionDescripcion = (prediction) => (
        prediction?.descripcion
        || prediction?.descripcionarticulo
        || prediction?.descripcionArticulo
        || prediction?.sDescripcion
        || prediction?.mensaje
        || ""
    ).trim();

    const handleEntrenarMuestras = async () => {
        if (!articuloSeleccionado || sampleProgress.active) return;

        setLoadingAprendizaje(true);
        setSampleProgress({ active: true, current: 0, total: 10 });

        for (let muestra = 1; muestra <= 10; muestra += 1) {
            setSampleProgress({ active: true, current: muestra, total: 10 });
            const imagenBase64 = capturarImagenBase64(0.82);
            if (imagenBase64) {
                await handleEnviarVectorEntrenamiento(imagenBase64, null, { medida: medidaSeleccionada, silent: muestra > 1 });
            }
            if (muestra < 5) {
                await esperar(1000);
            }
        }

        await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        setSampleProgress({ active: false, current: 0, total: 10 });
        setLoadingAprendizaje(false);
    };

    const handleEliminarTrainings = async () => {
        setLivePrediction(null);
        await handleEliminarEntrenamiento();
        if (articuloSeleccionado?.clavearticulo) {
            await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        }
        setLivePrediction(null);
    };

    const infoArticuloActual = useMemo(() => {
        if (!articuloSeleccionado || !infoAprendizaje.length) return null;
        
        const clave = (articuloSeleccionado.clavearticulo || '').trim();
        const found = infoAprendizaje.find(i => {
            const claveInfo = (i.clavearticulo || '').trim().toUpperCase();
            return claveInfo === clave.toUpperCase();
        });
        
        return found || null;
    }, [infoAprendizaje, articuloSeleccionado]);

    const porcentaje = Number(infoArticuloActual?.porcentaje ?? infoArticuloActual?.dPorcentaje ?? 0);
    const vectores = Number(infoArticuloActual?.totalvectores ?? infoArticuloActual?.totalVectores ?? infoArticuloActual?.iTotalVectores ?? 0);
    const vectoresV2 = Number(infoArticuloActual?.vectoresv2 ?? infoArticuloActual?.vectoresV2 ?? infoArticuloActual?.iVectoresV2 ?? 0);
    const medidasArticulo = useMemo(
        () => getMedidasArticulo(articuloSeleccionado),
        [articuloSeleccionado]
    );
    const medidasConteo = useMemo(() => {
        const mapa = {};
        for (const m of (infoArticuloActual?.medidas || [])) {
            if (m?.medida) mapa[String(m.medida).toUpperCase()] = Number(m.vectores || 0);
        }
        return mapa;
    }, [infoArticuloActual]);
    const avanceRed = entrenamientoRed.totales > 0
        ? Math.round((entrenamientoRed.procesadas / entrenamientoRed.totales) * 100)
        : 0;

    const labelObjetoIA = useMemo(() => {
        const clave = getPredictionClave(livePrediction);
        const descripcion = getPredictionDescripcion(livePrediction);
        const confianza = livePrediction?.confianza !== undefined
            ? ` ${Math.round(Number(livePrediction.confianza) * 100)}%`
            : "";

        if (clave || descripcion) {
            return `${clave || "IA"}${confianza}${descripcion ? ` - ${descripcion}` : ""}`;
        }

        return articuloSeleccionado?.clavearticulo
            ? `${articuloSeleccionado.clavearticulo?.trim()} - monitoreando`
            : "Objeto IA";
    }, [livePrediction, articuloSeleccionado?.clavearticulo]);

    const getStatusColor = (p) => {
        if (p >= 100) return 'success';
        if (p >= 50) return 'info';
        if (p > 0) return 'warning';
        return 'default';
    };

    return (
        <Fragment>
            <SizeSoftXLayout 
                titlePage="Entrenamiento IA - Computadora"
                buttonActions={
                    <MnuIAEntrenamientoActions
                        onAbrirBusquedaFoto={() => setOpenBusquedaFoto(true)}
                        onEntrenar={handleEnviarEntrenamiento}
                        onEntrenarMuestras={handleEntrenarMuestras}
                        onEntrenarModelo={handleEntrenarRed}
                        onEliminarEntrenamiento={handleEliminarTrainings}
                        entrenamientoActivo={sampleProgress.active || entrenamientoRed.activo}
                        disabledEntrenar={!articuloSeleccionado || !objectSelection}
                        disabledDelete={!articuloSeleccionado?.clavearticulo}
                    />
                }
            >
                <ModalBuscarArticulosSimple
                    openModal={statusBuscarArticuloSimple}
                    handleCloseModal={handleCerrarBuscarArticulo}
                    handleArticuloSelect={handleArticuloSelect}
                />

                <ModalBuscarPorFoto
                    open={openBusquedaFoto}
                    onClose={() => setOpenBusquedaFoto(false)}
                    onArticuloSelect={handleArticuloFromFoto}
                />
                
                <Box sx={{ p: 2, pb: 12 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                        <VideocamIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Cámara PC
                                        </Typography>
                                    </Stack>
                                    
                                    <Box
                                        onPointerDown={handleSeleccionObjetoStart}
                                        onPointerMove={handleSeleccionObjetoMove}
                                        onPointerUp={handleSeleccionObjetoEnd}
                                        onPointerCancel={handleSeleccionObjetoEnd}
                                        sx={{
                                            position: 'relative',
                                            bgcolor: '#000',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            maxHeight: '58vh',
                                            cursor: 'crosshair',
                                            touchAction: 'none',
                                        }}
                                    >
                                        <video 
                                            ref={videoRef} 
                                            autoPlay 
                                            playsInline 
                                            muted
                                            style={{ width: '100%', maxHeight: '58vh', objectFit: 'contain', display: 'block' }}
                                        />

                                        <CameraObjectSelectorOverlay selection={objectSelection} videoElement={videoRef.current} label={labelObjetoIA} />

                                        {sampleProgress.active && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        px: 3,
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: 'rgba(0,0,0,.72)',
                                                        color: '#fff',
                                                        fontWeight: 900,
                                                        fontSize: 26,
                                                        boxShadow: 4,
                                                        border: '2px solid #ffd600',
                                                    }}
                                                >
                                                    Muestra {sampleProgress.current} / {sampleProgress.total}
                                                </Box>
                                            </Box>
                                        )}

                                        {selectionDebugPreview && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 8,
                                                    bottom: 8,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 0.75,
                                                    borderRadius: 1,
                                                    bgcolor: 'rgba(0,0,0,.72)',
                                                    color: '#fff',
                                                    pointerEvents: 'none',
                                                    boxShadow: 3,
                                                }}
                                            >
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, lineHeight: 1 }}>
                                                        Gris
                                                    </Typography>
                                                    <img src={selectionDebugPreview.grayDataUrl} alt="Diagnóstico gris" width="78" height="78" style={{ objectFit: 'cover', border: '1px solid #90caf9' }} />
                                                </Box>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, lineHeight: 1 }}>
                                                        Bordes
                                                    </Typography>
                                                    <img src={selectionDebugPreview.edgeDataUrl} alt="Diagnóstico bordes" width="78" height="78" style={{ objectFit: 'cover', border: '1px solid #ffeb3b' }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 68 }}>
                                                    Centro {Math.round(selectionDebugPreview.centerXPct * 100)}% / {Math.round(selectionDebugPreview.centerYPct * 100)}%
                                                    <br />
                                                    Borde {selectionDebugPreview.edgeWeight}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {imagenCapturada && (
                                            <Box sx={{ 
                                                position: 'absolute', 
                                                top: 8, 
                                                right: 8, 
                                                width: 120, 
                                                border: '2px solid #fff', 
                                                borderRadius: 1,
                                                boxShadow: 2,
                                                bgcolor: '#fff'
                                            }}>
                                                <img 
                                                    src={imagenCapturada} 
                                                    alt="Capturada" 
                                                    style={{ width: '100%', display: 'block' }}
                                                />
                                            </Box>
                                        )}
                                        
                                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CameraAltIcon />}
                                onClick={handleAsignarFoto}
                                disabled={!articuloSeleccionado?.clavearticulo || loadingAprendizaje}
                                fullWidth
                                size="large"
                            >
                                Asignar foto al artículo
                            </Button>
                        </Grid>

                        {articuloSeleccionado && (
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={imagenArticulo ? 8 : 12}>
                                                <Typography variant="h6" gutterBottom>
                                                    Datos del Artículo
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Clave:</strong> {articuloSeleccionado.clavearticulo}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Descripción:</strong> {articuloSeleccionado.descripcion}
                                                </Typography>
                                                {articuloSeleccionado.marca && (
                                                    <Typography variant="body2">
                                                        <strong>Marca:</strong> {articuloSeleccionado.marca}
                                                    </Typography>
                                                )}
                                                {medidasArticulo.length > 0 && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                            Medida en entrenamiento (se aprenden las 3 base en el mismo flujo)
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 1 }}>
                                                            {medidasArticulo.map((med) => {
                                                                const conteo = medidasConteo[String(med).toUpperCase()] ?? 0;
                                                                const activa = String(medidaSeleccionada).toUpperCase() === String(med).toUpperCase();
                                                                return (
                                                                    <Chip
                                                                        key={med}
                                                                        label={`${med} (${conteo})`}
                                                                        color={activa ? "primary" : "default"}
                                                                        variant={activa ? "filled" : "outlined"}
                                                                        size="small"
                                                                        onClick={() => setMedidaSeleccionada(med)}
                                                                    />
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Grid>
                                            {imagenArticulo && (
                                                <Grid item xs={12} md={4}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <img 
                                                            src={imagenArticulo} 
                                                            alt="Artículo" 
                                                            style={{ 
                                                                width: '100%', 
                                                                maxHeight: '140px',
                                                                objectFit: 'contain',
                                                                borderRadius: 8,
                                                                border: '2px solid #1976d2'
                                                            }}
                                                        />
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>

                    {articuloSeleccionado && (
                        <Card
                            sx={{
                                position: 'sticky',
                                bottom: 12,
                                mt: 2,
                                zIndex: 20,
                                border: '1px solid #d7e3f5',
                                boxShadow: 5,
                            }}
                        >
                            <CardContent sx={{ py: 1.5 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PsychologyIcon fontSize="small" color="primary" /> Aprendizaje IA
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Vectores: <strong>{vectores}</strong> (v2: <strong>{vectoresV2}</strong>)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Red: <strong>v{metricasIA?.version ?? 0}</strong>
                                            {metricasIA ? ` · ${metricasIA.ramas ?? 0} ramas · ${metricasIA.vectores ?? 0} muestras` : ""}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                            <Typography variant="caption">Progreso del artículo</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>{porcentaje}%</Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={porcentaje}
                                            color={getStatusColor(porcentaje)}
                                            sx={{ height: 9, borderRadius: 5 }}
                                        />
                                        {sampleProgress.active && (
                                            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>
                                                Capturando muestra {sampleProgress.current} de {sampleProgress.total}
                                                {medidaSeleccionada ? ` · medida ${medidaSeleccionada}` : ""}
                                            </Typography>
                                        )}
                                        {entrenamientoRed.activo && (
                                            <Box sx={{ mt: 0.5 }}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                        Entrenando red {entrenamientoRed.procesadas}/{entrenamientoRed.totales}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{avanceRed}%</Typography>
                                                </Stack>
                                                <LinearProgress variant="determinate" value={avanceRed} color="secondary" sx={{ height: 7, borderRadius: 4 }} />
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                            {loadingAprendizaje && <CircularProgress size={18} />}
                                            <Chip
                                                icon={<PsychologyIcon />}
                                                label={porcentaje >= 100 ? "Modelo Optimizado" : "Requiere muestras"}
                                                color={getStatusColor(porcentaje)}
                                                size="small"
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </SizeSoftXLayout>
        </Fragment>
    );
}
